"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

const SHOW_TOOLBAR_DELAY_MS = 1500;
const HIDE_TOOLBAR_DELAY_MS = 300;

// PERFORMANCE: Split context into two parts:
// 1. Setters context (stable, never changes) - for nodes to SET hover state
// 2. State context (changes on hover) - only for components that need to READ hover state
// This prevents all 50 nodes from re-rendering when any node is hovered.

interface NodeHoverSettersContextValue {
  setHoveredNodeId: (id: string | null) => void;
  setSelectedNodeId: (id: string | null) => void;
  setIsDraggingNode: (isDragging: boolean) => void;
  setMultipleNodesSelected: (multiple: boolean) => void;
  setActiveToolbarNodeId: (id: string | null) => void;
}

interface NodeHoverStateContextValue {
  hoveredNodeId: string | null;
  selectedNodeId: string | null;
  isDraggingNode: boolean;
  /** True when 2+ nodes are selected — suppresses per-node description toolbars */
  multipleNodesSelected: boolean;
  /** The single node currently allowed to render a floating description toolbar. */
  activeToolbarNodeId: string | null;
}

// Context for setters only - stable, never causes re-renders
const NodeHoverSettersContext = createContext<NodeHoverSettersContextValue | null>(null);

// Context for state - only subscribe if you need to READ the current hover state
const NodeHoverStateContext = createContext<NodeHoverStateContextValue | null>(null);

export function NodeHoverProvider({ children }: { children: ReactNode }) {
  const [hoveredNodeId, setHoveredNodeIdState] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeIdState] = useState<string | null>(null);
  const [isDraggingNode, setIsDraggingNodeState] = useState<boolean>(false);
  const [multipleNodesSelected, setMultipleNodesSelectedState] = useState<boolean>(false);
  const [activeToolbarNodeId, setActiveToolbarNodeIdState] = useState<string | null>(null);

  // Memoize setters - these NEVER change, so components using only setters don't re-render
  const setHoveredNodeId = useCallback((id: string | null) => {
    setHoveredNodeIdState(id);
  }, []);

  const setSelectedNodeId = useCallback((id: string | null) => {
    setSelectedNodeIdState(id);
  }, []);

  const setIsDraggingNode = useCallback((isDragging: boolean) => {
    setIsDraggingNodeState(isDragging);
  }, []);

  const setMultipleNodesSelected = useCallback((multiple: boolean) => {
    setMultipleNodesSelectedState(multiple);
  }, []);

  const setActiveToolbarNodeId = useCallback((id: string | null) => {
    setActiveToolbarNodeIdState(id);
  }, []);

  // Setters context value - truly stable (callbacks never change).
  // IMPORTANT: isDraggingNode was previously included here as a "convenience read" but it's a
  // changing state value. Including it made this context unstable, causing ALL 20+ node components
  // to re-render on every drag start/stop (context changes bypass React.memo).
  // Components that need to READ isDraggingNode should use useNodeHoverState() instead.
  const settersValue = useMemo(
    () => ({
      setHoveredNodeId,
      setSelectedNodeId,
      setIsDraggingNode,
      setMultipleNodesSelected,
      setActiveToolbarNodeId,
    }),
    [setHoveredNodeId, setSelectedNodeId, setIsDraggingNode, setMultipleNodesSelected, setActiveToolbarNodeId],
  );

  // State context value - changes when hover/selection/dragging changes
  const stateValue = useMemo(
    () => ({ hoveredNodeId, selectedNodeId, isDraggingNode, multipleNodesSelected, activeToolbarNodeId }),
    [hoveredNodeId, selectedNodeId, isDraggingNode, multipleNodesSelected, activeToolbarNodeId],
  );

  return (
    <NodeHoverSettersContext.Provider value={settersValue}>
      <NodeHoverStateContext.Provider value={stateValue}>{children}</NodeHoverStateContext.Provider>
    </NodeHoverSettersContext.Provider>
  );
}

/**
 * Hook to SET hover/selection state.
 * PERFORMANCE: Components using this hook do NOT re-render when hover changes.
 * Use this in node components that need to call setHoveredNodeId/setSelectedNodeId.
 */
export function useNodeHoverSetters() {
  const context = useContext(NodeHoverSettersContext);
  if (!context) {
    return {
      setHoveredNodeId: () => {},
      setSelectedNodeId: () => {},
      setIsDraggingNode: () => {},
      setMultipleNodesSelected: () => {},
      setActiveToolbarNodeId: () => {},
    };
  }
  return context;
}

/**
 * Hook to READ hover/selection state.
 * WARNING: Components using this hook WILL re-render when hover changes.
 * Only use this in components that actually need to know the current hover state.
 */
export function useNodeHoverState() {
  const context = useContext(NodeHoverStateContext);
  if (!context) {
    return {
      hoveredNodeId: null,
      selectedNodeId: null,
      isDraggingNode: false,
      multipleNodesSelected: false,
      activeToolbarNodeId: null,
    };
  }
  return context;
}

/**
 * @deprecated Use useNodeHoverSetters() for setting and useNodeHoverState() for reading.
 * This hook causes re-renders on hover changes.
 */
export function useNodeHover() {
  const setters = useNodeHoverSetters();
  const state = useNodeHoverState();
  return { ...setters, ...state };
}

interface UseNodeDescriptionVisibleResult {
  showToolbar: boolean;
  setToolbarHovered: (hovered: boolean) => void;
  canShowSelectedToolbar: boolean;
}

/**
 * Hook to determine if a node's description toolbar should be visible
 * Shows toolbar when:
 * - Node is hovered for at least 1.5 seconds (intent-based)
 * Hides toolbar when hover ends (with delay to allow moving to toolbar)
 * Keeps toolbar visible while the toolbar itself is being hovered
 *
 * PERFORMANCE: Subscribes to isDraggingNode via useNodeHoverState() to suppress the
 * show-timer during drags. React Flow uses setPointerCapture on its container during
 * drags, which suppresses pointerleave/mouseleave on the dragged node — so isHovered
 * stays true and the 1500 ms timer would otherwise fire and pop up the toolbar mid-drag.
 * This hook is only used by toolbar-rendering components (not the 50+ node components),
 * so the extra re-render on drag start/stop does not reintroduce the previous regression.
 */
export function useNodeDescriptionVisible(
  nodeId: string,
  isHovered: boolean,
  isSelected: boolean,
): UseNodeDescriptionVisibleResult {
  const { isDraggingNode, activeToolbarNodeId } = useNodeHoverState();
  const { setActiveToolbarNodeId } = useNodeHoverSetters();
  const [showToolbar, setShowToolbar] = useState(false);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeToolbarNodeIdRef = useRef<string | null>(activeToolbarNodeId);

  useEffect(() => {
    activeToolbarNodeIdRef.current = activeToolbarNodeId;
  }, [activeToolbarNodeId]);

  const setToolbarHovered = useCallback((hovered: boolean) => {
    setIsToolbarHovered(hovered);
  }, []);

  useEffect(() => {
    // Treat an in-progress drag as "not hovered" — React Flow's pointer capture
    // suppresses mouseleave so isHovered stays true, but we must not show the toolbar.
    const isActivelyHovered = !isDraggingNode && (isHovered || isToolbarHovered);
    const isToolbarOwner = activeToolbarNodeId === nodeId;
    const otherToolbarVisible = activeToolbarNodeId !== null && activeToolbarNodeId !== nodeId;

    if (isActivelyHovered) {
      // Cancel any pending hide timer
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      // If toolbar is already visible (e.g., hovering toolbar), keep it visible
      if (showToolbar) {
        if (!isToolbarOwner) {
          setActiveToolbarNodeId(nodeId);
        }
        return;
      }

      // Start a 1.5s timer to show the toolbar (only if hovering node, not toolbar)
      if (isHovered && !showTimerRef.current && !otherToolbarVisible) {
        showTimerRef.current = setTimeout(() => {
          const currentToolbarOwner = activeToolbarNodeIdRef.current;
          if (currentToolbarOwner && currentToolbarOwner !== nodeId) {
            showTimerRef.current = null;
            return;
          }
          setActiveToolbarNodeId(nodeId);
          setShowToolbar(true);
          showTimerRef.current = null;
        }, SHOW_TOOLBAR_DELAY_MS);
      }
    } else {
      // Not hovered - clear show timer and start a delay before hiding
      // This gives user time to move their mouse to the toolbar
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      // Only start hide timer if toolbar is currently visible
      if (showToolbar && !hideTimerRef.current) {
        hideTimerRef.current = setTimeout(() => {
          setShowToolbar(false);
          if (activeToolbarNodeIdRef.current === nodeId) {
            setActiveToolbarNodeId(null);
          }
          hideTimerRef.current = null;
        }, HIDE_TOOLBAR_DELAY_MS);
      }
    }

    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      // Don't clear hide timer in cleanup - let it complete
    };
  }, [activeToolbarNodeId, isDraggingNode, isHovered, isToolbarHovered, nodeId, setActiveToolbarNodeId, showToolbar]);

  useEffect(() => {
    if (activeToolbarNodeId !== null && activeToolbarNodeId !== nodeId && showToolbar) {
      setShowToolbar(false);
    }
  }, [activeToolbarNodeId, nodeId, showToolbar]);

  return {
    showToolbar,
    setToolbarHovered,
    canShowSelectedToolbar: isSelected && (activeToolbarNodeId === null || activeToolbarNodeId === nodeId),
  };
}

/**
 * Hook to check if any node description toolbar is currently visible
 * Used by WorkflowSummaryBanner to hide when toolbar is shown
 *
 * NOTE: This hook DOES subscribe to state and will cause re-renders.
 * Only use in components that need to react to hover changes (like banners).
 */
export function useIsAnyToolbarVisible(): boolean {
  const { hoveredNodeId, selectedNodeId } = useNodeHoverState();

  // Toolbar is visible if a node is hovered OR selected (and no other node is hovered)
  return hoveredNodeId !== null || selectedNodeId !== null;
}
